import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Typography,
  Spin,
  Alert,
  Card,
  Segmented,
  Badge,
  Table,
  Empty,
  DatePicker,
  Button,
} from "antd";
import {
  getBankAccountD,
  getBankAccountWalletSnapShots,
  getBankAccountBalanceSnapShots,
} from "../../api/bankAccountApi";
import { deleteManualTransaction } from "../../api/bankReportStatementApi";
import {
  getBankAccountStatements,
  getBankAccountReportStatements,
  downloadBankStatementExcel,
  downloadBankReportStatementExcel,
} from "../../api/balanceApi";
import { DownloadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  BankAccountCard,
  InstitutionCard,
} from "../../components/reportCards/reportCards";
import { createBankStatementColumns } from "../../components/statements/BankStatements";
import { createReportStatementColumns } from "../../components/statements/ReportStatements";
import { formatMoney, formatAccountNumber } from "../../utils/formatUtils";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const VIEW_MODE = {
  GMAIL: "GMAIL",
  STATEMENTS: "STATEMENTS",
  REPORT_STATEMENTS: "REPORT_STATEMENTS",
};

function getBankLabel(bank) {
  if (!bank) return "-";
  return bank
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getInstitutionTypeLabel(type) {
  if (!type) return "-";
  return type
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseRowDate(value) {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const parsed = dayjs(
      new Date(year, (month || 1) - 1, day || 1, hour, minute, second)
    );
    return parsed.isValid() ? parsed : null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function getRowDateValue(item, viewMode) {
  if (!item) return null;

  if (viewMode === VIEW_MODE.REPORT_STATEMENTS) {
    return (
      item.transactionDate ??
      item.statementDate ??
      item.date ??
      item.createdAt ??
      null
    );
  }

  return (
    item.statementDate ??
    item.transactionDate ??
    item.date ??
    item.createdAt ??
    null
  );
}

function getSnapshotDateValue(item) {
  if (!item) return null;

  return (
    item.periodDate ??
    item.snapshotDate ??
    item.statementDate ??
    item.transactionDate ??
    item.date ??
    item.createdAt ??
    null
  );
}

function getSnapshotOpenValue(item) {
  if (!item) return 0;

  return Number(
    item.startBalance ??
      item.openBalance ??
      item.openingBalance ??
      item.beginBalance ??
      0
  );
}

function getSnapshotCloseValue(item) {
  if (!item) return 0;

  return Number(
    item.closeBalance ??
      item.endBalance ??
      item.closingBalance ??
      item.finalBalance ??
      0
  );
}

function decodeRfc2047Q(value) {
  const match = value.match(/^=\?UTF-8\?Q\?(.*)\?=$/i);
  if (!match?.[1]) return value;

  try {
    const encodedText = match[1]
      .replace(/_/g, " ")
      .replace(/=([A-Fa-f0-9]{2})/g, "%$1");

    return decodeURIComponent(encodedText);
  } catch {
    return value;
  }
}

function cleanupFilename(filename, fallback) {
  if (!filename || typeof filename !== "string") return fallback;

  let cleaned = filename.trim();
  cleaned = cleaned.replace(/^["']|["']$/g, "");
  cleaned = decodeRfc2047Q(cleaned);

  return cleaned || fallback;
}

function extractFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return cleanupFilename(decodeURIComponent(utf8Match[1]), fallback);
    } catch {
      return cleanupFilename(utf8Match[1], fallback);
    }
  }

  const genericStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (genericStarMatch?.[1]) {
    const rawValue = genericStarMatch[1].trim().replace(/^["']|["']$/g, "");
    const parts = rawValue.split("''");

    if (parts.length === 2) {
      try {
        return cleanupFilename(decodeURIComponent(parts[1]), fallback);
      } catch {
        return cleanupFilename(parts[1], fallback);
      }
    }

    return cleanupFilename(rawValue, fallback);
  }

  const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return cleanupFilename(quotedMatch[1], fallback);

  const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (plainMatch?.[1]) return cleanupFilename(plainMatch[1], fallback);

  return fallback;
}

function buildDateMeta(item, viewMode, index) {
  const rawDateValue = getRowDateValue(item, viewMode);
  const parsedDate = parseRowDate(rawDateValue);

  return {
    ...item,
    __rowKey: item.id ?? `${viewMode}-${index}`,
    __rawDateValue: rawDateValue,
    __parsedDate: parsedDate,
    __dateKey: parsedDate ? parsedDate.format("YYYY-MM-DD") : "NO_DATE",
    __displayDate: parsedDate ? parsedDate.format("YYYY.MM.DD") : "Огноо байхгүй",
    __sortValue: parsedDate ? parsedDate.valueOf() : -1,
  };
}

function AccountPage() {
  const { bankAccountId } = useParams();
  const navigate = useNavigate();

  const [fetchedData, setFetchedData] = useState(null);
  const [gmailStatements, setGmailStatements] = useState([]);
  const [statements, setStatements] = useState([]);
  const [reportStatements, setReportStatements] = useState([]);
  const [walletSnapshots, setWalletSnapshots] = useState([]);
  const [balanceSnapshots, setBalanceSnapshots] = useState([]);
  const [sortOrder, setSortOrder] = useState("DESC");

  const [viewMode, setViewMode] = useState(VIEW_MODE.GMAIL);
  const [dateRange, setDateRange] = useState([null, null]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        accRes,
        stmtRes,
        reportRes,
        walletSnapshotRes,
        balanceSnapshotRes,
      ] = await Promise.all([
        getBankAccountD(bankAccountId),
        getBankAccountStatements(bankAccountId),
        getBankAccountReportStatements(bankAccountId),
        getBankAccountWalletSnapShots(bankAccountId, { timeFrame: "DAILY" }),
        getBankAccountBalanceSnapShots(bankAccountId, { timeFrame: "DAILY" }),
      ]);

      setFetchedData(accRes ?? null);

      setGmailStatements(
        Array.isArray(accRes?.gmailStatements) ? accRes.gmailStatements : []
      );

      setStatements(Array.isArray(stmtRes) ? stmtRes : []);

      setReportStatements(
        Array.isArray(reportRes) ? reportRes : reportRes ? [reportRes] : []
      );

      setWalletSnapshots(
        Array.isArray(walletSnapshotRes) ? walletSnapshotRes : []
      );

      setBalanceSnapshots(
        Array.isArray(balanceSnapshotRes) ? balanceSnapshotRes : []
      );
    } catch (err) {
      console.error(err);
      setError("Мэдээлэл ачааллахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  }, [bankAccountId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const institution = useMemo(
    () => fetchedData?.institution ?? null,
    [fetchedData]
  );

  const account = useMemo(
    () => fetchedData?.bankAccount ?? null,
    [fetchedData]
  );

  const activeData = useMemo(() => {
    const raw =
      viewMode === VIEW_MODE.GMAIL
        ? gmailStatements
        : viewMode === VIEW_MODE.REPORT_STATEMENTS
          ? reportStatements
          : statements;

    return (raw || []).map((item, index) =>
      buildDateMeta(item, viewMode, index)
    );
  }, [viewMode, gmailStatements, reportStatements, statements]);

  const filteredData = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return activeData;

    const start = dateRange[0].startOf("day");
    const end = dateRange[1].endOf("day");

    return activeData.filter((item) => {
      if (!item.__parsedDate) return false;

      return (
        (item.__parsedDate.isAfter(start) || item.__parsedDate.isSame(start)) &&
        (item.__parsedDate.isBefore(end) || item.__parsedDate.isSame(end))
      );
    });
  }, [activeData, dateRange]);

  const activeSnapshotMap = useMemo(() => {
    const source =
      viewMode === VIEW_MODE.REPORT_STATEMENTS
        ? walletSnapshots
        : balanceSnapshots;

    const map = new Map();

    (source || []).forEach((item) => {
      const parsedDate = parseRowDate(getSnapshotDateValue(item));
      if (!parsedDate) return;

      const key = parsedDate.format("YYYY-MM-DD");
      const existing = map.get(key);

      if (!existing || item.current) {
        map.set(key, item);
      }
    });

    return map;
  }, [viewMode, walletSnapshots, balanceSnapshots]);

  const groupedData = useMemo(() => {
    const groupedMap = filteredData.reduce((acc, item) => {
      const key = item.__dateKey || "NO_DATE";

      if (!acc[key]) {
        acc[key] = {
          dateKey: key,
          displayDate: item.__displayDate || "Огноо байхгүй",
          rows: [],
          sortValue: item.__sortValue ?? -1,
        };
      }

      acc[key].rows.push(item);

      if ((item.__sortValue ?? -1) > (acc[key].sortValue ?? -1)) {
        acc[key].sortValue = item.__sortValue;
      }

      return acc;
    }, {});

    return Object.values(groupedMap)
      .sort((a, b) => {
        if (a.dateKey === "NO_DATE") return 1;
        if (b.dateKey === "NO_DATE") return -1;

        if (sortOrder === "ASC") {
          return (a.sortValue ?? -1) - (b.sortValue ?? -1);
        }

        return (b.sortValue ?? -1) - (a.sortValue ?? -1);
      })
      .map((group) => {
        const snapshot = activeSnapshotMap.get(group.dateKey);

        const sortedRows = [...group.rows].sort((a, b) => {
          if (sortOrder === "ASC") {
            return (a.__sortValue ?? -1) - (b.__sortValue ?? -1);
          }

          return (b.__sortValue ?? -1) - (a.__sortValue ?? -1);
        });

        return {
          ...group,
          rows: sortedRows,
          snapshot,
          openValue: getSnapshotOpenValue(snapshot),
          closeValue: getSnapshotCloseValue(snapshot),
        };
      });
  }, [filteredData, activeSnapshotMap, sortOrder]);

  const handleClearDateFilter = () => {
    setDateRange([null, null]);
  };

  const handleDelete = async (id) => {
    try {
      await deleteManualTransaction(id);

      setReportStatements((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      console.error(err);
      alert("Устгахад алдаа гарлаа");
    }
  };

  const handleDownload = async () => {
    try {
      if (!filteredData.length) return;

      if (viewMode === VIEW_MODE.GMAIL) {
        alert("Gmail гүйлгээний Excel татах API холбогдоогүй байна");
        return;
      }

      const bank = account?.bank;

      if (!bank) {
        alert("Банк олдсонгүй");
        return;
      }

      const startDate = dateRange?.[0]
        ? dayjs(dateRange[0]).format("YYYY-MM-DD")
        : null;

      const endDate = dateRange?.[1]
        ? dayjs(dateRange[1]).format("YYYY-MM-DD")
        : null;

      const result =
        viewMode === VIEW_MODE.REPORT_STATEMENTS
          ? await downloadBankReportStatementExcel(
              bankAccountId,
              bank,
              startDate,
              endDate
            )
          : await downloadBankStatementExcel(
              bankAccountId,
              bank,
              startDate,
              endDate
            );

      const fallbackFilename =
        viewMode === VIEW_MODE.REPORT_STATEMENTS
          ? `${bank}_report_statement_${bankAccountId}.xlsx`
          : `${bank}_statement_${bankAccountId}.xlsx`;

      const filename = extractFilename(
        result.contentDisposition,
        fallbackFilename
      );

      const url = window.URL.createObjectURL(result.blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Файл татахад алдаа гарлаа");
    }
  };

  const columns = useMemo(() => {
    if (viewMode === VIEW_MODE.REPORT_STATEMENTS) {
      return createReportStatementColumns({
        currency: account?.currency,
        onDelete: handleDelete,
      });
    }

    return createBankStatementColumns({
      currency: account?.currency,
      onDelete: handleDelete,
    });
  }, [viewMode, account?.currency]);

  const titleText =
    viewMode === VIEW_MODE.GMAIL
      ? "Gmail хуулга"
      : viewMode === VIEW_MODE.REPORT_STATEMENTS
        ? "Хийсвэр хуулга"
        : "Ориг Хуулга";

  const emptyText =
    viewMode === VIEW_MODE.GMAIL
      ? "Gmail гүйлгээ олдсонгүй"
      : viewMode === VIEW_MODE.REPORT_STATEMENTS
        ? "Тайлангийн гүйлгээ олдсонгүй"
        : "Гүйлгээ олдсонгүй";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <Alert type="error" message={error} showIcon />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        Буцах
      </Button>

      <div className="mb-6">
        <Title level={3} className="!mb-1">
          Банкны дансны тойм
        </Title>
      </div>

      <Row gutter={[16, 16]}>
        {/* Increased width */}
        <Col xs={24} xl={16}>
          <BankAccountCard
            account={account}
            formatAccountNumber={formatAccountNumber}
            formatMoney={formatMoney}
            getBankLabel={getBankLabel}
          />
        </Col>

        {/* Reduced institution width */}
        <Col xs={24} xl={8}>
          <InstitutionCard
            institution={institution}
            getInstitutionTypeLabel={getInstitutionTypeLabel}
          />
        </Col>
      </Row>

      <Card variant={false} className="mt-4 rounded-xl shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-end">
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                {
                  label: `Gmail гүйлгээ (${gmailStatements.length})`,
                  value: VIEW_MODE.GMAIL,
                },
                {
                  label: `Гүйлгээ (${statements.length})`,
                  value: VIEW_MODE.STATEMENTS,
                },
                {
                  label: `Тайлан (хийсвэр) гүйлгээ (${reportStatements.length})`,
                  value: VIEW_MODE.REPORT_STATEMENTS,
                },
              ]}
            />

            <RangePicker
              value={dateRange}
              onChange={(values) => setDateRange(values ?? [null, null])}
              allowClear
            />

            <Button onClick={handleClearDateFilter}>Цэвэрлэх</Button>

            <Badge
              color={
                viewMode === VIEW_MODE.REPORT_STATEMENTS
                  ? "processing"
                  : viewMode === VIEW_MODE.GMAIL
                    ? "warning"
                    : "default"
              }
              text={`Нийт ${filteredData.length}-с ${activeData.length}-г харуулж байна`}
            />

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              disabled={!filteredData.length}
            >
              Excel татах
            </Button>
          </div>
        </div>
      </Card>

      <Card variant={false} className="mt-4 rounded-xl shadow-sm">
        <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
          <div>
            <Text strong className="text-base">
              {titleText}
            </Text>

            <div>
              <Text type="secondary">{filteredData.length} бичилт байна.</Text>
            </div>
          </div>

          <div className="flex gap-3">
            <Segmented
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { label: "Шинэ → Хуучин", value: "DESC" },
                { label: "Хуучин → Шинэ", value: "ASC" },
              ]}
            />
          </div>
        </div>

        {!groupedData.length ? (
          <Empty description={emptyText} />
        ) : (
          <div className="space-y-6">
            {groupedData.map((group) => (
              <Card
                key={group.dateKey}
                size="small"
                className="rounded-xl border border-slate-200 shadow-sm"
                title={
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <Text strong className="text-base">
                      {group.displayDate}
                    </Text>

                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="text-black">
                        Эхний үлдэгдэл:{" "}
                        {formatMoney(group.openValue, account?.currency)}
                      </div>

                      <div className="text-black">
                        Хаалтын үлдэгдэл:{" "}
                        {formatMoney(group.closeValue, account?.currency)}
                      </div>

                      <Text type="secondary">
                        {group.rows.length} бичлэг
                        {group.rows.length !== 1 ? "үүд" : ""}
                      </Text>
                    </div>
                  </div>
                }
              >
                <Table
                  rowKey={(record) => record.__rowKey}
                  columns={columns}
                  dataSource={group.rows}
                  pagination={false}
                  scroll={{ x: 1200 }}
                />
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default AccountPage;